import { useState, useCallback } from 'react';
import { MapPin, Plus, Pencil, Trash2 } from 'lucide-react';
import { useApi } from '../hooks/useApi.js';
import { post, put, del } from '../lib/api.js';

interface KnownLocation {
  id: string;
  name: string;
  ip_ranges: string[];
  created_at: string;
  updated_at: string;
}

export function KnownLocationsPage() {
  const { data, loading, error, refresh } = useApi<{ data: KnownLocation[] }>('/protect/conditional-access/known-locations');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Partial<KnownLocation> & { ip_ranges_text?: string }>({});
  const [saving, setSaving] = useState(false);

  const locations = data?.data ?? [];

  const handleEdit = (loc: KnownLocation) => {
    setEditing({ ...loc, ip_ranges_text: loc.ip_ranges.join('\n') });
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditing({ name: '', ip_ranges_text: '' });
    setShowModal(true);
  };

  const handleDelete = useCallback(async (id: string) => {
    if (!confirm('Delete this location?')) return;
    await del(`/protect/conditional-access/known-locations/${id}`);
    refresh();
  }, [refresh]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const ipRanges = (editing.ip_ranges_text ?? '').split('\n').map(s => s.trim()).filter(Boolean);
      const body = { name: editing.name, ip_ranges: ipRanges };
      if (editing.id) {
        await put(`/protect/conditional-access/known-locations/${editing.id}`, body);
      } else {
        await post('/protect/conditional-access/known-locations', body);
      }
      setShowModal(false);
      refresh();
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-gray-500">Loading...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Known Locations</h2>
          <p className="text-sm text-gray-500 mt-1">Define trusted office IP ranges for conditional access policies</p>
        </div>
        <button onClick={handleCreate} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium">
          <Plus className="h-4 w-4" /> Add Location
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Ranges</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {locations.map(loc => (
              <tr key={loc.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-gray-900 text-sm">{loc.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {loc.ip_ranges.map(range => (
                      <span key={range} className="inline-flex px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded font-mono">
                        {range}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {new Date(loc.updated_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => handleEdit(loc)} className="text-gray-400 hover:text-gray-600">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(loc.id)} className="text-gray-400 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {locations.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-500 text-sm">No known locations configured</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {editing.id ? 'Edit Location' : 'Add Location'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editing.name ?? ''}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="e.g., Main Office"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IP Ranges (one per line, CIDR notation)</label>
                <textarea
                  value={editing.ip_ranges_text ?? ''}
                  onChange={(e) => setEditing({ ...editing, ip_ranges_text: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  rows={4}
                  placeholder="10.0.0.0/8&#10;192.168.1.0/24"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !editing.name}
                className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : editing.id ? 'Update' : 'Add Location'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
